import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager";
import bodyStylesManager from "../../../stores/body-styles-manager";
import { IVideoBlockData } from "../../../types/content-block-data";

import SatoriTextBox from "../shared/satori-ui/textbox";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import { cleanDimenValues } from "../../../lib/page-builder-lib";

/**
 * VideoOptions – Sidebar Editing Panel for Video Blocks
 *
 * Provides a sidebar panel for configuring the URL and padding of a Video content block
 * in the Satori PageBuilder. Supports common embed video platforms (YouTube, Vimeo, etc.)
 * and allows responsive spacing with px/rem/em unit conversion.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Video content block to edit.
 *
 * @example
 * // Render video options panel for a given Video block
 * <VideoOptions blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Video block's data from the MobX contentManager store.
 * - Renders controls for editing the video URL (supports YouTube, Vimeo, direct links, etc).
 * - Padding control supports px/rem/em units with auto conversion based on parent font size.
 * - All field changes call `contentManager.setVideoConfig` for atomic and reactive MobX state updates.
 * - Fully reactive via MobX's `observer()` wrapper.
 * - Mirrors the options panel structure of ImageOptions, TextStyles, and HeadingStyles for consistent UX.
 *
 * @see contentManager.setVideoConfig
 * @see ImageOptions, TextStyles, HeadingStyles – For parallel sidebar editing panels
 */
export default observer(function VideoOptions({blockId}: {blockId: string}) {
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "video") return null;
  const videoData = block.blockData as IVideoBlockData;

	const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
  let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)

  if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
    parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
  else
    parentRawFontSize = parseInt(parentFontSize!.value);

  function update(field: string, value: any) {
    contentManager.setVideoConfig(blockId, {
      ...videoData,
      [field]: value,
    });
  }

  return (
    <section className="flex flex-col p-4 gap-3">
      <SatoriTextBox
        label={"Url"}
        value={videoData.url || "#"}
        onChange={val => update("url", val)}
      />
      <SatoriDimensionsBox
        label={"Padding"}
        value={cleanDimenValues(videoData.padding.value)}
        unit={videoData.padding.unit}
        onValueChange={val => 
          update("padding", {
            value: val,
            unit: videoData.padding.unit,
          })
        }
        onUnitChange={unitVal => {
          if (unitVal === "px") {
            // We need to change from rem/em value to pixel value
            const convertedValue = Math.round(
              parseFloat(videoData?.padding?.value) * parentRawFontSize
            ).toString();
            update("padding", {
              value: convertedValue,
              unit: unitVal,
            })
          } else if (unitVal === "rem" || unitVal === "em") {
            const convertedValue = (
              parseFloat(videoData?.padding?.value) / parentRawFontSize
            ).toFixed(3).toString();
            update("padding", {
              value: convertedValue,
              unit: unitVal,
            })
          }
        }}
      />
    </section>
  );
});
